import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../QuillEditor.css';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CreditCardForm from '../components/CreditCardForm';

const sanitize = (val) => (val && String(val).trim() !== '' ? val : undefined);

// Prefix relative URLs with your admin host
const prefixIfRelative = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://app.yaper.co${url.startsWith('/') ? '' : '/'}${url}`;
};

const getFirst = (obj, paths) => {
  for (const p of paths) {
    const v = p
      .split('.')
      .reduce((o, k) => (o == null ? undefined : o[k]), obj);
    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  }
  return undefined;
};

const normalizeCard = (c = {}) => {
  const card = c.card || {};
  const bank = c.bank || card.bank || {};

  const name =
    getFirst(c, ['name']) ??
    getFirst(card, ['name', 'display_name', 'long_name', 'short_name']) ??
    '';

  const bankNameRaw = getFirst(c, [
    'bank_name',
    'card.bank_name',
    'bank.bank_name',
    'bank.name',
    'card.bank.name',
  ]);

  const bankDescRaw = getFirst(c, [
    'bank_description',
    'card.bank_description',
    'bank.bank_description',
    'bank.description',
    'card.bank.description',
  ]);

  const bankLogoRaw = getFirst(c, [
    'bank_logo',
    'bank_url',
    'bank_logo_url',
    'card.bank_logo',
    'card.bank_url',
    'card.bank_logo_url',
    'bank.logo',
    'bank.logo_url',
    'bank.bank_logo',
    'bank.url',
    'bank.image_url',
    'card.bank.logo',
    'card.bank.logo_url',
  ]);

  const cardImageRaw = getFirst(c, [
    '?column?',
    'card_image',
    'card.card_image',
    'card.image_url',
  ]);

  return {
    id: c.id ?? card.id ?? null,
    name,
    affiliate: c.affiliate || card.affiliate || '',
    rating:
      typeof c.rating === 'number'
        ? c.rating
        : Number((c.rating ?? card.rating) || 0),

    cardImage: prefixIfRelative(cardImageRaw || ''),
    bankLogo: prefixIfRelative(bankLogoRaw || ''),
    bankName: bankNameRaw || '',
    bankDescription: bankDescRaw || '',
  };
};

const CATEGORY_OPTIONS = [
  'News',
  'Offer',
  'Review',
  'Guide',
  'Comparison',
  'Cashback',
  'Rewards',
  'Miles',
  'Travel',
  'Lounge',
  'Dining',
  'Shopping',
  'Fuel',
  'Grocery',
  'Online',
  'Business',
  'Education',
  'EMI',
  'Fees',
];

const styles = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#ffffff',
  },
  container: {
    width: '80%',
    maxWidth: '1200px',
    padding: '2rem 0 4rem',
    fontFamily: 'Inter, system-ui, Arial, sans-serif',
  },
  actionsBar: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '0.9rem 0',
    background: '#ffffff',
    boxShadow: '0 1px 0 0 rgba(15,23,42,0.08), 0 2px 8px rgba(15,23,42,0.06)',
  },
  divider: {
    height: '3px',
    width: '100%',
    borderRadius: '999px',
    background:
      'linear-gradient(90deg, #2563EB 0%, #7C3AED 33%, #EC4899 66%, #10B981 100%)',
    opacity: 0.9,
    marginBottom: '1.2rem',
  },
  button: {
    padding: '12px 18px',
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 600,
  },
  secondaryBtn: {
    padding: '12px 18px',
    backgroundColor: '#111827',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 600,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0.75rem 0 1rem',
  },
  heading: {
    margin: 0,
    color: '#111827',
    fontSize: '1.5rem',
    fontWeight: 700,
    letterSpacing: '0.2px',
  },
  form: { display: 'grid', gridTemplateColumns: '1fr', gap: '1.6rem' },
  section: {
    padding: '1.35rem',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    boxShadow: '0 6px 14px rgba(0,0,0,0.05)',
  },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem' },
  rowTall: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2.25rem',
    marginTop: '1rem',
    marginBottom: '1.25rem',
  },
  fullWidth: {
    marginTop: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  control: { display: 'flex', flexDirection: 'column', gap: '0.7rem' },
  label: {
    fontSize: '0.95rem',
    color: '#1f2937',
    fontWeight: 700,
    letterSpacing: '0.2px',
    marginTop: '0.15rem',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #cfd8e3',
    borderRadius: '10px',
    boxSizing: 'border-box',
    background: '#fff',
    fontSize: '0.95rem',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '12px',
    border: '1px solid #cfd8e3',
    borderRadius: '10px',
    background: '#fff',
    fontSize: '0.95rem',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    minHeight: '140px',
    padding: '12px',
    border: '1px solid #cfd8e3',
    borderRadius: '10px',
    resize: 'vertical',
    fontSize: '0.95rem',
  },
  textareaWide: {
    width: '100%',
    minHeight: '160px',
    padding: '12px',
    border: '1px solid #cfd8e3',
    borderRadius: '10px',
    resize: 'vertical',
    fontSize: '0.95rem',
    marginBottom: '1.25rem',
  },
  quillContainer: {
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    overflow: 'visible',
    position: 'relative',
    zIndex: 0,
  },
  suggestWrap: { position: 'relative' },
  suggestList: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 10,
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    boxShadow: '0 10px 18px rgba(0,0,0,0.08)',
    marginTop: '6px',
    maxHeight: '220px',
    overflowY: 'auto',
  },
  suggestItem: {
    padding: '10px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  suggestItemHover: { background: '#f3f4f6' },
  thumb: {
    width: 40,
    height: 24,
    objectFit: 'cover',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
  },
  logo: {
    width: 24,
    height: 24,
    objectFit: 'cover',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
  },
  tbWrap: {
    marginTop: '1rem',
    padding: '1rem',
    border: '1px dashed #d1d5db',
    borderRadius: 12,
    background: '#fafafa',
  },
  tbRow: { gap: '0.75rem', marginTop: '0.5rem', display: 'grid' },
  smallBtn: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    background: '#fff',
    cursor: 'pointer',
  },
  dangerBtn: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #fecaca',
    background: '#fee2e2',
    color: '#991b1b',
    cursor: 'pointer',
  },
  muted: { fontSize: 12, color: '#6b7280' },
  previewBox: {
    marginTop: '0.75rem',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '0.75rem',
    overflowX: 'auto',
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: '220px',
    objectFit: 'contain',
    marginTop: '12px',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
  },
  bankPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px',
    border: '1px dashed #e5e7eb',
    borderRadius: 10,
    background: '#fafafa',
    marginTop: '8px',
  },
  chipsWrap: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: '999px',
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
    fontSize: 12,
    color: '#1e3a8a',
  },
  chipBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1,
    color: '#1e3a8a',
  },
};

export default function NewBlog({ blogs }) {
  const navigate = useNavigate();
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('editId');

  const [form, setForm] = useState({
    heading: '',
    content: '',
    previewImage: '',
    previewText: '',
    youtubeLink: '',
    rating: 0,
    card_id: 1,
    source_id: '1',
    priority: 1,
    tags: '',
    categories: [],
    status: 1,
    assets: [],
    affiliate: '',
    cardImage: '',
    bankLogo: '',
    bankName: '',
    bankDescription: '',
  });

  const [meta, setMeta] = useState({
    details: '',
    overview: '',
    benefits: '',
    fees: '',
  });
  const [loading, setLoading] = useState(false);
  const [previewImageFile, setPreviewImageFile] = useState(null);
  const [focused, setFocused] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(-1);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState('');
  const [networks, setNetworks] = useState([]);
  const [banks, setBanks] = useState([]);

  const quillRefs = {
    content: useRef(null),
    previewText: useRef(null),
    metaDetails: useRef(null),
    benefits: useRef(null),
  };

  const [tables, setTables] = useState([
    {
      id: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()),
      title: 'Table 1',
      hasHeader: true,
      cols: ['Label', 'Value'],
      rows: [['', '']],
      delimiter: ',',
      rowSeparator: ';',
      binding: 'table_1',
    },
  ]);
  const [activeTableId, setActiveTableId] = useState(tables[0].id);
  const getActiveTableIndex = () =>
    tables.findIndex((t) => t.id === activeTableId);
  const getActiveTable = () => tables[getActiveTableIndex()] || tables[0];

  const setActiveTable = (updater) => {
    setTables((prev) => {
      const idx = prev.findIndex((t) => t.id === activeTableId);
      if (idx === -1) return prev;
      const clone = [...prev];
      clone[idx] =
        typeof updater === 'function' ? updater(clone[idx]) : updater;
      return clone;
    });
  };

  const addNewTable = () => {
    const t = {
      id: crypto?.randomUUID
        ? crypto.randomUUID()
        : String(Date.now() + Math.random()),
      title: `Table ${tables.length + 1}`,
      hasHeader: true,
      cols: ['Label', 'Value'],
      rows: [['', '']],
      delimiter: ',',
      rowSeparator: ';',
      binding: `table_${tables.length + 1}`,
    };
    setTables((prev) => [...prev, t]);
    setActiveTableId(t.id);
  };

  const removeTable = (id) => {
    setTables((prev) => {
      const newList = prev.filter((t) => t.id !== id);
      if (activeTableId === id && prev.length > 1) {
        const next = newList[0];
        if (next) setActiveTableId(next.id);
      }
      return newList;
    });
  };

  const ensureRowWidth = (colsCount) => (row) => {
    const copy = [...row];
    while (copy.length < colsCount) copy.push('');
    while (copy.length > colsCount) copy.pop();
    return copy;
  };

  const updateActive = (patch) => setActiveTable((t) => ({ ...t, ...patch }));
  const addColumn = () =>
    setActiveTable((t) => ({
      ...t,
      cols: [...t.cols, `Col ${t.cols.length + 1}`],
      rows: t.rows.map((r) => [...r, '']),
    }));
  const removeColumn = (idx) =>
    setActiveTable((t) => ({
      ...t,
      cols: t.cols.filter((_, i) => i !== idx),
      rows: t.rows.map((r) => r.filter((_, i) => i !== idx)),
    }));
  const updateHeader = (idx, val) =>
    setActiveTable((t) => ({
      ...t,
      cols: t.cols.map((h, i) => (i === idx ? val : h)),
    }));
  const addTbRow = () =>
    setActiveTable((t) => ({
      ...t,
      rows: [...t.rows, Array(t.cols.length).fill('')],
    }));
  const removeTbRow = (i) =>
    setActiveTable((t) => ({
      ...t,
      rows: t.rows.filter((_, idx) => idx !== i),
    }));
  const updateTbCell = (rIdx, cIdx, val) =>
    setActiveTable((t) => ({
      ...t,
      rows: t.rows.map((r, i) =>
        i === rIdx ? r.map((c, j) => (j === cIdx ? val : c)) : r
      ),
    }));

  const uploadBase64ImagesToS3 = async (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const imgs = Array.from(doc.querySelectorAll('img'));

    const uploadPromises = imgs.map(async (img) => {
      const src = img.src;

      // Skip if already an S3/http URL
      if (src.startsWith('http')) return;

      // Only process base64 images
      if (!src.startsWith('data:image')) return;

      try {
        // Convert base64 to blob
        const res = await fetch(src);
        const blob = await res.blob();
        const ext = blob.type.split('/')[1] || 'png'; // e.g. "png", "jpeg"
        const file = new File([blob], `image.${ext}`, { type: blob.type });

        // Upload to S3
        const s3Url = await uploadAsset(file);

        // Replace base64 src with S3 URL in the HTML
        img.src = s3Url;
      } catch (err) {
        console.error('Failed to upload image to S3:', err);
      }
    });

    await Promise.all(uploadPromises);

    // Return cleaned HTML with S3 URLs
    return doc.body.innerHTML;
  };

  useEffect(() => {
    setActiveTable((t) => ({
      ...t,
      rows: t.rows.map(ensureRowWidth(t.cols.length)),
    }));
  }, [tables.length, activeTableId]);

  const escHTML = (s = '') =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  const escCSV = (s = '') => {
    const needsQuotes = /[",\n;]/.test(s) || s.includes(' ');
    const doubled = String(s).replace(/"/g, '""');
    return needsQuotes ? `"${doubled}"` : doubled;
  };

  const buildTableHTML = (table) => {
    const nonEmptyRows = table.rows.filter((r) =>
      r.some((c) => (c ?? '').trim() !== '')
    );
    if (!nonEmptyRows.length || table.cols.length === 0) return '';

    const header = table.hasHeader
      ? `<thead><tr>${table.cols
          .map((h) => `<th>${escHTML(h)}</th>`)
          .join('')}</tr></thead>`
      : '';

    const body = `<tbody>${nonEmptyRows
      .map((r) => `<tr>${r.map((c) => `<td>${escHTML(c)}</td>`).join('')}</tr>`)
      .join('')}</tbody>`;

    const titleBlock = table.title?.trim()
      ? `<caption style="caption-side:top;text-align:left;font-weight:600;margin:0 0 .5rem 0">${escHTML(
          table.title.trim()
        )}</caption>`
      : '';

    return `
      <div class="ql-table-wrapper" data-table-id="${table.id}">
        <table class="ql-table">
          ${titleBlock}
          ${header}
          ${body}
        </table>
      </div>
    `;
  };

  const buildDelimited = (table) => {
    const nonEmptyRows = table.rows.filter((r) =>
      r.some((c) => (c ?? '').trim() !== '')
    );
    if (!nonEmptyRows.length || table.cols.length === 0) return '';
    const d = table.delimiter || ',';
    const rs = table.rowSeparator || ';';

    const header = table.hasHeader ? table.cols.map(escCSV).join(d) : '';
    const rows = nonEmptyRows.map((r) => r.map(escCSV).join(d));
    const parts = header ? [header, ...rows] : rows;
    return parts.join(` ${rs} `);
  };

  const buildMetaBlock = (table) => {
    const binding = (table.binding || 'table_1').trim();
    const title = (table.title || '').trim();
    const delimited = buildDelimited(table) || '';
    return `• {${binding}} — "${title}"\n\n  ${delimited}\n\n`;
  };

  const insertActiveTableIntoMeta = () => {
    const table = getActiveTable();
    const quill = quillRefs.metaDetails?.current?.getEditor?.();
    if (!table || !quill) {
      alert('Meta editor not ready. Click into it, then try again.');
      return;
    }
    const block = buildMetaBlock(table);
    const idx = quill.getSelection()?.index ?? quill.getLength();
    quill.insertText(idx, block, 'user');
  };

  const insertActiveTableIntoContent = () => {
    const table = getActiveTable();
    const quill = quillRefs.content?.current?.getEditor?.();
    if (!table || !quill) {
      alert('Content editor not ready. Click into it, then try again.');
      return;
    }
    const block = buildMetaBlock(table);
    const idx = quill.getSelection()?.index ?? quill.getLength();
    quill.insertText(idx, block, 'user');
  };

  const insertActiveTableIntoRates = () => {
    const block = buildMetaBlock(getActiveTable());
    setMeta((prev) => ({
      ...prev,
      fees: (prev.fees || '').trim() ? `${prev.fees}\n\n${block}` : block,
    }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setCardsLoading(true);
        setCardsError('');
        const res = await fetch('https://app.yaper.co/api/v1/cards/get_cards');
        let list = [];
        if (res.ok) {
          const json = await res.json();
          list = Array.isArray(json?.cards) ? json.cards : [];
        }
        if (!Array.isArray(list) || list.length === 0) {
          try {
            const alt = await fetch('https://app.yaper.co/api/v1/cards');
            if (alt.ok) {
              const j = await alt.json();
              list = Array.isArray(j?.cards)
                ? j.cards
                : Array.isArray(j)
                  ? j
                  : [];
            }
          } catch {}
        }
        const normalized = (list || [])
          .map(normalizeCard)
          .filter((c) => c.name);
        if (!cancelled) setCards(normalized);
      } catch (e) {
        if (!cancelled) setCardsError('Failed to load card suggestions.');
        console.error('Cards fetch failed:', e);
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('https://app.yaper.co/api/v3/card_network/list');
        if (r.ok) {
          const j = await r.json();
          if (!cancelled)
            setNetworks(
              Array.isArray(j?.data) ? j.data : Array.isArray(j) ? j : []
            );
        }
      } catch (e) {
        console.warn('Network list fetch failed', e);
      }
      try {
        const r = await fetch('https://app.yaper.co/api/v3/bank/list');
        if (r.ok) {
          const j = await r.json();
          if (!cancelled)
            setBanks(
              Array.isArray(j?.data) ? j.data : Array.isArray(j) ? j : []
            );
        }
      } catch (e) {
        console.warn('Bank list fetch failed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const uploadAsset = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(
      'https://app.yaper.co/api/v1/content_pages/upload_asset',
      { method: 'POST', body: formData }
    );
    if (!res.ok) throw new Error(`Server responded with status: ${res.status}`);
    const data = await res.json();
    return data.url;
  };

  const imageHandler = (quillRef) => () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*, video/*');
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const assetUrl = await uploadAsset(file);
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        if (file.type.startsWith('image/'))
          quill.insertEmbed(range.index, 'image', assetUrl);
        else if (file.type.startsWith('video/'))
          quill.insertEmbed(range.index, 'video', assetUrl);
      } catch {
        alert('Asset upload failed. Please try again.');
      }
    };
  };

  const previewImageHandler = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPreviewImageFile(file);
      const url = URL.createObjectURL(file);
      setForm((prev) => ({ ...prev, previewImage: url }));
    }
  };

  const getModules = (quillRef) => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [
          { list: 'ordered' },
          { list: 'bullet' },
          { indent: '-1' },
          { indent: '+1' },
        ],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: { image: imageHandler(quillRef) },
    },
  });

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
  ];

  const contentModules = useMemo(
    () => getModules(quillRefs.content),
    [quillRefs.content]
  );
  const previewTextModules = useMemo(
    () => getModules(quillRefs.previewText),
    [quillRefs.previewText]
  );
  const metaDetailsModules = useMemo(
    () => getModules(quillRefs.metaDetails),
    [quillRefs.metaDetails]
  );
  const benefitsModules = useMemo(
    () => getModules(quillRefs.benefits),
    [quillRefs.benefits]
  ); // CC only

  useEffect(() => {
    if (editId && Array.isArray(blogs)) {
      const blogToEdit = blogs.find((b) => String(b.id) === editId);
      if (blogToEdit) {
        const { meta: oldMeta = {}, ...formValues } = blogToEdit;
        setForm({
          heading: formValues.heading || '',
          content: formValues.content || '',
          previewImage: formValues.previewImage || formValues.preview_img || '',
          previewText: formValues.previewText || formValues.preview_text || '',
          youtubeLink: formValues.youtubeLink || formValues.youtube_link || '',
          rating: formValues.rating || 0,
          card_id: formValues.card_id || 1,
          source_id: formValues.source_id || '1',
          // IMPORTANT: preserve 0, don't coalesce to 1
          priority: formValues.priority ?? 1,
          tags: formValues.tags || '',
          categories: Array.isArray(formValues.categories)
            ? formValues.categories
            : [],
          status: formValues.status || 1,
          assets: formValues.assets || [],
          affiliate: formValues.affiliate || '',
          cardImage: formValues.cardImage || formValues.card_image || '',
          bankLogo: prefixIfRelative(
            formValues.bankLogo || formValues.bank_logo || ''
          ),
          bankName: formValues.bankName || formValues.bank_name || '',
          bankDescription:
            formValues.bankDescription || formValues.bank_description || '',
        });
        try {
          const parsedMeta =
            typeof oldMeta === 'string' ? JSON.parse(oldMeta) : oldMeta;
          setMeta({
            details: parsedMeta.details || '',
            overview: parsedMeta.overview || '',
            benefits: parsedMeta.benefits || '',
            fees: parsedMeta.fees || '',
          });
          if (Array.isArray(parsedMeta?.tables)) {
            setTables(
              parsedMeta.tables.map((t) => ({
                id:
                  t.id ||
                  (crypto?.randomUUID
                    ? crypto.randomUUID()
                    : String(Date.now() + Math.random())),
                title: t.title || 'Table',
                hasHeader: !!t.hasHeader,
                cols:
                  Array.isArray(t.cols) && t.cols.length
                    ? t.cols
                    : ['Label', 'Value'],
                rows:
                  Array.isArray(t.rows) && t.rows.length ? t.rows : [['', '']],
                delimiter: t.delimiter || ',',
                rowSeparator: t.rowSeparator || ';',
                binding:
                  t.binding || `table_${Math.floor(Math.random() * 1000)}`,
              }))
            );
            setActiveTableId(parsedMeta.tables[0]?.id || tables[0]?.id);
          }
        } catch {
          setMeta({ details: '', overview: '', benefits: '', fees: '' });
        }
      }
    }
  }, [editId, blogs]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'previewImage') setPreviewImageFile(null);
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    setForm((prev) => {
      if (prev.categories.includes(v)) return prev;
      return { ...prev, categories: [...prev.categories, v] };
    });
    e.target.value = '';
  };

  const removeCategory = (value) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.filter((c) => c !== value),
    }));
  };

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setMeta((prev) => ({ ...prev, [name]: value }));
  };

  const extractImageUrls = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || '', 'text/html');
    return Array.from(doc.querySelectorAll('img')).map((img) => img.src);
  };

  const filteredSuggestions = useMemo(() => {
    const q = (form.heading || '').trim().toLowerCase();
    if (!q) return [];
    return cards.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 6);
  }, [form.heading, cards]);

  const applySuggestion = (item) => {
    setForm((prev) => ({
      ...prev,
      card_id: item.id || prev.card_id,
      heading: item.name,
      affiliate: item.affiliate || prev.affiliate,
      rating: item.rating ?? prev.rating,
      cardImage: item.cardImage || prev.cardImage,
      bankLogo: item.bankLogo || prev.bankLogo,
      bankName: item.bankName || prev.bankName,
      bankDescription: item.bankDescription || prev.bankDescription,
    }));
    setFocused(false);
  };

  useEffect(() => {
    if (type !== 'cc') return;
    const h = (form.heading || '').trim().toLowerCase();
    if (!h || !cards.length) return;

    const exact = cards.find((c) => (c.name || '').trim().toLowerCase() === h);
    if (exact) {
      setForm((prev) => ({
        ...prev,
        card_id: exact.id || prev.card_id,
        bankLogo: exact.bankLogo || prev.bankLogo,
        bankName: exact.bankName || prev.bankName,
        bankDescription: exact.bankDescription || prev.bankDescription,
      }));
    }
  }, [form.heading, cards, type]);

  const handlePreview = () => {
    navigate('/PreviewBlog', {
      state: {
        content_type: (type === 'cc' ? 'cc_blog' : type) || 'blog',
        form,
        meta,
      },
    });
  };

  const slugify = (text = '') =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 100);

  const syncCardSilently = async () => {
    try {
      if (form.card_id) {
        await fetch('https://app.yaper.co/api/v1/cards/update_data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: Number(form.card_id),
            card_type: 'credit',
          }),
        });
        return form.card_id;
      }
      if (sanitize(form.heading)) {
        const payload = {
          card_type: 'debit',
          slug: slugify(form.heading),
          url: sanitize(form.cardImage),
          short_name: form.heading?.slice(0, 40),
          display_name: form.heading,
          keywords: form.heading,
        };
        if (
          Array.isArray(networks) &&
          networks.length &&
          (networks[0]?.id || networks[0]?.value)
        ) {
          payload.card_network_id = Number(
            networks[0]?.id ?? networks[0]?.value
          );
        }
        if (
          Array.isArray(banks) &&
          banks.length &&
          (banks[0]?.id || banks[0]?.value)
        ) {
          payload.bank_id = Number(banks[0]?.id ?? banks[0]?.value);
        }
        const res = await fetch('https://app.yaper.co/api/v1/cards', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const j = await res.json();
          const newId = j?.id || j?.data?.id;
          if (newId) setForm((prev) => ({ ...prev, card_id: newId }));
          return newId;
        }
      }
    } catch (e) {
      console.warn('Silent card sync failed:', e);
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let finalPreviewImageUrl = form.previewImage;
    if (previewImageFile) {
      try {
        finalPreviewImageUrl = await uploadAsset(previewImageFile);
      } catch (err) {
        console.error('Failed to upload preview image:', err.message);
        alert('Failed to publish blog. Please try again.');
        setLoading(false);
        return;
      }
    }

    let processedContent = form.content;
    try {
      processedContent = await uploadBase64ImagesToS3(form.content);
      // Update form state so Quill shows S3 URLs too
      setForm((prev) => ({ ...prev, content: processedContent }));
    } catch (err) {
      console.error('Failed to process content images:', err);
    }

    const imgUrls = extractImageUrls(processedContent);
    await syncCardSilently();

    // Ensure youtube link is always sent as an explicit string (empty string if none)
    const youtube = (form.youtubeLink ?? '').trim();

    const tablesPayload = tables.map((t) => ({
      id: t.id,
      title: t.title,
      hasHeader: t.hasHeader,
      cols: t.cols,
      rows: t.rows,
      binding: t.binding,
      delimiter: t.delimiter,
      rowSeparator: t.rowSeparator,
      delimited: buildDelimited(t),
    }));

    const generatedSlug = slugify(form.heading);

    const newBlog = {
      heading: sanitize(form.heading) || 'Untitled',
      slug: generatedSlug,
      content: sanitize(processedContent),
      preview_text: sanitize(form.previewText),
      tags: sanitize(form.tags),
      categories: Array.isArray(form.categories) ? form.categories : [],
      // Send explicit string values for youtube so backend receives the key even when empty
      youtube_link: youtube,
      youtubeLink: youtube, // optional: keeps camelCase copy for frontend round-trip
      card_id: Number(form.card_id) || 1,
      priority: String(form.priority) === '0' ? 0 : 1,
      source_id: form.source_id || '1',
      content_type: type === 'cc' ? 'cc_blog' : type,
      assets: imgUrls.length ? imgUrls : [],
      preview_img: sanitize(finalPreviewImageUrl),
      meta: {
        details: sanitize(meta.details),
        overview: sanitize(meta.overview),
        benefits: sanitize(meta.benefits),
        fees: sanitize(meta.fees),
        rating: Number(form.rating) || 0,
        card_id: Number(form.card_id) || 1,
        tables: tablesPayload,
        details_csv: tablesPayload.map((t) => t.delimited),
        table_keys: tables.map((t) => t.binding),
      },
      status: form.status || 1,
      affiliate: sanitize(form.affiliate),
      rating: Number(form.rating) || 0,
      card_image: sanitize(form.cardImage),
      bank_logo: sanitize(form.bankLogo),
      bank_name: sanitize(form.bankName),
      bank_description: sanitize(form.bankDescription),
    };

    // Debug payload to verify youtube is present in the POST body
    console.log('Publishing blog - payload:', newBlog);

    try {
      const res = await fetch('https://app.yaper.co/api/v1/content_pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBlog),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Server responded with ${res.status}: ${errText}`);
      }
      await res.json();
      navigate('/');
    } catch (err) {
      console.error('POST failed:', err.message);
      alert('Failed to publish blog. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.actionsBar}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            style={styles.button}
            title="Publish"
          >
            {loading ? 'Saving…' : 'Publish'}
          </button>
        </div>
        <div style={styles.divider} />

        <div style={styles.headerRow}>
          <h2 style={styles.heading}>
            Create {String(type || '').toUpperCase()} Blog
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.section}>
            <div style={styles.row}>
              <div style={styles.control}>
                <label style={styles.label}>Source</label>
                <select
                  name="source_id"
                  value={form.source_id}
                  onChange={handleFormChange}
                  style={styles.select}
                >
                  <option value="1">Yaper</option>
                  <option value="2">Cnc</option>
                  <option value="3">Card Academy</option>
                </select>
              </div>
              <div style={styles.control}>
                <label style={styles.label}>Status</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleFormChange}
                  style={styles.select}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>

            <div style={styles.rowTall}>
              <div style={styles.control}>
                <label style={styles.label}>Priority (Trending)</label>
                <select
                  name="priority"
                  value={String(form.priority)}
                  onChange={handleFormChange}
                  style={styles.select}
                >
                  <option value="0">Non-trending</option>
                  <option value="1">Trending</option>
                </select>
              </div>
              <div />
            </div>

            <div style={styles.rowTall}>
              <div style={styles.control}>
                <label style={styles.label}>Preview Image (Upload)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={previewImageHandler}
                  style={styles.input}
                />
              </div>
              <div style={styles.control}>
                <label style={styles.label}>Preview Image URL</label>
                <input
                  type="text"
                  name="previewImage"
                  value={form.previewImage}
                  onChange={handleFormChange}
                  placeholder="Or paste an image URL"
                  style={styles.input}
                />
              </div>
            </div>

            {form.previewImage && (
              <img
                src={form.previewImage}
                alt="Preview"
                style={styles.previewImage}
              />
            )}
          </div>

          {type === 'cc' && (
            // <div style={styles.section}>
            //   <h4 style={{ ...styles.label, marginBottom: '0.5rem' }}>
            //     Card Directory
            //   </h4>
            //   <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
            //     {cardsLoading
            //       ? 'Loading cards…'
            //       : cardsError
            //       ? cardsError
            //       : `Loaded ${cards.length} cards`}
            //   </div>
            //   <div style={styles.rowTall}>
            //     <div style={{ ...styles.control, ...styles.suggestWrap }}>
            //       <label style={styles.label}>Card Name</label>
            //       <input
            //         name="heading"
            //         value={form.heading}
            //         onChange={handleFormChange}
            //         onFocus={() => setFocused(true)}
            //         onBlur={() => setTimeout(() => setFocused(false), 150)}
            //         placeholder="Start typing to search…"
            //         style={styles.input}
            //       />
            //       {focused && filteredSuggestions.length > 0 && (
            //         <div style={styles.suggestList}>
            //           {filteredSuggestions.map((s, i) => (
            //             <div
            //               key={`${s.id}-${s.name}-${i}`}
            //               style={{
            //                 ...styles.suggestItem,
            //                 ...(hoverIndex === i
            //                   ? styles.suggestItemHover
            //                   : {}),
            //               }}
            //               onMouseEnter={() => setHoverIndex(i)}
            //               onMouseLeave={() => setHoverIndex(-1)}
            //               onMouseDown={(e) => {
            //                 e.preventDefault();
            //                 applySuggestion(s);
            //               }}
            //             >
            //               <img
            //                 src={s.cardImage || 'https://placehold.co/80x48'}
            //                 alt=""
            //                 style={styles.thumb}
            //               />
            //               <div style={{ flex: 1 }}>
            //                 <div style={{ fontWeight: 600 }}>{s.name}</div>
            //                 <div style={{ fontSize: 12, color: '#6b7280' }}>
            //                   {s.affiliate || '—'} • {s.rating || 0}★
            //                 </div>
            //                 {s.bankName ? (
            //                   <div style={{ fontSize: 12, color: '#374151' }}>
            //                     Bank: {s.bankName}
            //                   </div>
            //                 ) : null}
            //                 {s.bankDescription ? (
            //                   <div style={{ fontSize: 11, color: '#6b7280' }}>
            //                     {s.bankDescription}
            //                   </div>
            //                 ) : null}
            //               </div>
            //               {s.bankLogo ? (
            //                 <img src={s.bankLogo} alt="" style={styles.logo} />
            //               ) : null}
            //             </div>
            //           ))}
            //         </div>
            //       )}
            //     </div>

            //     <div style={styles.control}>
            //       <label style={styles.label}>Affiliate (Link)</label>
            //       <input
            //         name="affiliate"
            //         value={form.affiliate}
            //         onChange={handleFormChange}
            //         placeholder="https://…"
            //         style={styles.input}
            //       />
            //     </div>
            //   </div>

            //   <div style={styles.rowTall}>
            //     <div style={styles.control}>
            //       <label style={styles.label}>Rating</label>
            //       <input
            //         name="rating"
            //         type="number"
            //         min="0"
            //         max="5"
            //         step="0.1"
            //         value={form.rating}
            //         onChange={handleFormChange}
            //         placeholder="0–5"
            //         style={styles.input}
            //       />
            //     </div>
            //     <div style={styles.control}>
            //       <label style={styles.label}>Card Image URL</label>
            //       <input
            //         name="cardImage"
            //         value={form.cardImage}
            //         onChange={handleFormChange}
            //         placeholder="https://…"
            //         style={styles.input}
            //       />
            //     </div>
            //   </div>

            //   <div style={styles.row}>
            //     <div style={styles.control}>
            //       <label style={styles.label}>Bank Logo URL</label>
            //       <input
            //         name="bankLogo"
            //         value={form.bankLogo}
            //         onChange={handleFormChange}
            //         placeholder="https://… "
            //         style={styles.input}
            //       />
            //       <div style={styles.bankPreview}>
            //         <img
            //           src={prefixIfRelative(form.bankLogo) || 'https://...'}
            //           alt=""
            //           style={{ width: 36, height: 36, objectFit: 'contain' }}
            //         />
            //         <div>
            //           <div style={{ fontWeight: 600 }}>
            //             {form.bankName || '—'}
            //           </div>
            //           {form.bankDescription ? (
            //             <div style={{ fontSize: 12, color: '#6b7280' }}>
            //               {form.bankDescription}
            //             </div>
            //           ) : null}
            //         </div>
            //       </div>
            //     </div>

            //     <div style={styles.control}>
            //       <label style={styles.label}>Bank Name</label>
            //       <input
            //         name="bankName"
            //         value={form.bankName}
            //         onChange={handleFormChange}
            //         placeholder="e.g., HDFC Bank"
            //         style={styles.input}
            //       />
            //     </div>
            //   </div>
            // </div>
            <CreditCardForm
              form={form}
              handleFormChange={handleFormChange}
              cards={cards}
              cardsLoading={cardsLoading}
              cardsError={cardsError}
              filteredSuggestions={filteredSuggestions}
              focused={focused}
              setFocused={setFocused}
              hoverIndex={hoverIndex}
              setHoverIndex={setHoverIndex}
              applySuggestion={applySuggestion}
              styles={styles}
            />
          )}

          {(type === 'static' || type === 'blog') && (
            <div style={styles.section}>
              <div style={styles.control}>
                <label style={styles.label}>Heading</label>
                <input
                  name="heading"
                  value={form.heading}
                  onChange={handleFormChange}
                  placeholder="Heading"
                  style={styles.input}
                />
              </div>
              {type === 'blog' && (
                <>
                  <label style={styles.label}>Preview Text</label>
                  <div style={styles.quillContainer}>
                    <ReactQuill
                      ref={quillRefs.previewText}
                      value={form.previewText}
                      onChange={(val) =>
                        setForm((prev) => ({ ...prev, previewText: val }))
                      }
                      modules={previewTextModules}
                      formats={formats}
                    />
                  </div>
                </>
              )}
              <label style={{ ...styles.label, marginTop: '1rem' }}>
                Main Content
              </label>
              <div style={styles.quillContainer}>
                <ReactQuill
                  ref={quillRefs.content}
                  value={form.content}
                  onChange={(val) =>
                    setForm((prev) => ({ ...prev, content: val }))
                  }
                  modules={contentModules}
                  formats={formats}
                />
              </div>
            </div>
          )}

          {/* CC-ONLY: Meta + Table Builder + Benefits Quill + Rates & Fees */}
          {type === 'cc' && (
            <div style={styles.section}>
              <div style={{ ...styles.rowTall }}>
                <div style={styles.control}>
                  <label style={styles.label}>Card Name (Heading)</label>
                  <input
                    name="heading"
                    value={form.heading}
                    onChange={handleFormChange}
                    placeholder="Card Name (Used as heading)"
                    style={styles.input}
                  />
                </div>
                <div style={styles.control}>
                  <label style={styles.label}>YouTube Link</label>
                  <input
                    name="youtubeLink"
                    value={form.youtubeLink}
                    onChange={handleFormChange}
                    placeholder="YouTube Link"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.rowTall}>
                <div style={styles.control}>
                  <label style={styles.label}>Tags (comma-separated)</label>
                  <input
                    name="tags"
                    value={form.tags}
                    onChange={handleFormChange}
                    placeholder="tag1, tag2"
                    style={styles.input}
                  />
                </div>
                <div style={styles.control}>
                  <label style={styles.label}>Category</label>
                  <select
                    defaultValue=""
                    onChange={handleCategoryChange}
                    style={styles.select}
                  >
                    <option value="">— Select category —</option>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  {form.categories.length > 0 && (
                    <div style={styles.chipsWrap}>
                      {form.categories.map((cat) => (
                        <span key={cat} style={styles.chip}>
                          {cat}
                          <button
                            type="button"
                            style={styles.chipBtn}
                            aria-label={`Remove ${cat}`}
                            onClick={() => removeCategory(cat)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.rowTall}>
                <div style={styles.control}>
                  <label style={styles.label}>Rating (0–5)</label>
                  <input
                    name="rating"
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={form.rating}
                    onChange={handleFormChange}
                    placeholder="e.g. 4.5"
                    style={styles.input}
                  />
                </div>
                <div />
              </div>
              <div style={styles.rowTall}>
                <div style={styles.control}>
                  <label style={styles.label}>Card Details</label>
                  <textarea
                    name="overview"
                    value={meta.overview || ''}
                    onChange={handleMetaChange}
                    placeholder="Card Details"
                    style={styles.textarea}
                  />
                </div>
                <div style={styles.control}>
                  <label style={styles.label}>Benefits &amp; Features</label>
                  <div className="quill-wrap" style={styles.quillContainer}>
                    <ReactQuill
                      ref={quillRefs.benefits}
                      value={meta.benefits || ''}
                      onChange={(val) =>
                        setMeta((prev) => ({ ...prev, benefits: val ?? '' }))
                      }
                      modules={benefitsModules}
                      formats={formats}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>

              {/* CC: Rates & Fees (textarea) */}
              <label style={styles.label}>Rates &amp; Fees</label>
              <textarea
                name="fees"
                value={meta.fees || ''}
                onChange={handleMetaChange}
                style={styles.textareaWide}
              />

              <div style={styles.fullWidth}>
                <label style={styles.label}>Full Details </label>
                <div style={{ minHeight: 160 }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={meta.details ?? ''}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setMeta((prev) => ({
                        ...prev,
                        details: data,
                      }));
                    }}
                    config={{
                      toolbar: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'insertTable',
                        '|',
                        'undo',
                        'redo',
                      ],
                      table: {
                        contentToolbar: [
                          'tableColumn',
                          'tableRow',
                          'mergeTableCells',
                        ],
                      },
                    }}
                  />
                </div>

                {/* <div style={{ ...styles.quillContainer, minHeight: 160 }}>
                  <ReactQuill
                    key="meta-editor"
                    ref={quillRefs.metaDetails}
                    value={meta.details ?? ''}
                    onChange={val => setMeta(prev => ({ ...prev, details: val ?? '' }))}
                    modules={metaDetailsModules}
                    formats={formats}
                    theme="snow"
                  />
                </div> */}
                {/* ---------- /CC TABLE BUILDER ---------- */}
              </div>
            </div>
          )}

          {/* NORMAL BLOG: table builder solely for inserting into Main Content */}
          {type === 'blog' && (
            <div style={styles.section}>
              <div style={styles.tbWrap}>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    marginBottom: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={styles.label}>Table Builder</div>
                  <div className="muted" style={styles.muted}>
                    Click Insert to add the block to Main Content.
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    marginBottom: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <select
                    value={activeTableId}
                    onChange={(e) => setActiveTableId(e.target.value)}
                    style={styles.select}
                  >
                    {tables.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title || t.id}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    style={styles.smallBtn}
                    onClick={addNewTable}
                  >
                    + New table
                  </button>
                  {tables.length > 1 && (
                    <button
                      type="button"
                      style={styles.dangerBtn}
                      onClick={() => removeTable(activeTableId)}
                    >
                      Delete current
                    </button>
                  )}
                </div>

                {(() => {
                  const t = getActiveTable();
                  return (
                    <>
                      <div
                        style={{
                          display: 'grid',
                          gap: 12,
                          alignItems: 'center',
                          marginBottom: 8,
                          gridTemplateColumns: '1fr auto auto auto',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <span className="muted" style={styles.muted}>
                            Binding
                          </span>
                          <input
                            style={{ ...styles.input, width: 140 }}
                            placeholder="table_1"
                            value={t.binding}
                            onChange={(e) =>
                              updateActive({
                                binding: e.target.value.replace(/\s+/g, '_'),
                              })
                            }
                          />
                        </div>

                        <input
                          style={{ ...styles.input, width: '100%' }}
                          placeholder="Optional table title (e.g., Table 1)"
                          value={t.title}
                          onChange={(e) =>
                            updateActive({ title: e.target.value })
                          }
                        />
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={t.hasHeader}
                            onChange={(e) =>
                              updateActive({ hasHeader: e.target.checked })
                            }
                          />
                          <span>Include header</span>
                        </label>
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            justifyContent: 'flex-end',
                          }}
                        >
                          <button
                            type="button"
                            style={styles.smallBtn}
                            onClick={addColumn}
                          >
                            + Add column
                          </button>
                          <button
                            type="button"
                            style={styles.smallBtn}
                            onClick={addTbRow}
                          >
                            + Add row
                          </button>
                        </div>
                      </div>

                      {/* Header editors */}
                      <div
                        style={{
                          ...styles.tbRow,
                          gridTemplateColumns: `repeat(${t.cols.length}, 1fr) auto`,
                        }}
                      >
                        {t.cols.map((h, idx) => (
                          <div
                            key={`h-${idx}`}
                            style={{ display: 'flex', gap: 6 }}
                          >
                            <input
                              style={{ ...styles.input, flex: 1 }}
                              placeholder={`Header ${idx + 1}`}
                              value={h}
                              onChange={(e) =>
                                updateHeader(idx, e.target.value)
                              }
                            />
                          </div>
                        ))}
                        <div />
                      </div>

                      {/* Column remove buttons */}
                      <div
                        style={{
                          ...styles.tbRow,
                          gridTemplateColumns: `repeat(${t.cols.length}, 1fr) auto`,
                        }}
                      >
                        {t.cols.map((_, idx) => (
                          <button
                            key={`hc-${idx}`}
                            type="button"
                            style={styles.dangerBtn}
                            onClick={() => removeColumn(idx)}
                            disabled={t.cols.length <= 1}
                          >
                            Remove column {idx + 1}
                          </button>
                        ))}
                        <div />
                      </div>

                      {/* Data rows */}
                      {t.rows.map((r, rIdx) => (
                        <div
                          key={`r-${rIdx}`}
                          style={{
                            ...styles.tbRow,
                            gridTemplateColumns: `repeat(${t.cols.length}, 1fr) auto`,
                          }}
                        >
                          {t.cols.map((_, cIdx) => (
                            <input
                              key={`c-${rIdx}-${cIdx}`}
                              style={styles.input}
                              placeholder={`Row ${rIdx + 1} · Col ${cIdx + 1}`}
                              value={r[cIdx] ?? ''}
                              onChange={(e) =>
                                updateTbCell(rIdx, cIdx, e.target.value)
                              }
                            />
                          ))}
                          <button
                            type="button"
                            style={styles.dangerBtn}
                            onClick={() => removeTbRow(rIdx)}
                          >
                            Remove row
                          </button>
                        </div>
                      ))}

                      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button
                          type="button"
                          style={styles.smallBtn}
                          onClick={insertActiveTableIntoContent}
                          disabled={!quillRefs.content?.current}
                        >
                          Insert block into Content
                        </button>
                      </div>

                      <div style={{ marginTop: 12 }}>
                        <div className="muted" style={styles.muted}>
                          HTML preview
                        </div>
                        <div
                          style={styles.previewBox}
                          dangerouslySetInnerHTML={{
                            __html: buildTableHTML(t),
                          }}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
